import { useSupportedCountries } from '../../util/platformAccess';
import { TextField, MenuItem } from '@mui/material';
import React, { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import "../../assets/css/login.css";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Box,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import jwtDecode from "jwt-decode";
import CenteredGoogleLogin from './CenteredGoogleLogin';
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useTimeZones } from "../../util/timezones";
import { canonicalTimezone, findTimezoneOption } from '../../util/timezoneOptions';
import SignupTimezone from './SignupTimezone';
import { detectSignupCountry } from '../../util/signupCountry';
import { SIGNUP_USER, SIGNUP_USER_GOOGLE } from "../../graphql/mutations";
import GettingStartedGuide from "../common/gettingStartedGuide";
import { getLoginDestination, getSafeReturnTo, setToken } from "../../util/authUtils";
import { toFiniteIntOrNull } from "../../util/timezoneUtils";

function SignUp(props) {
  const { timeZones, loading: timezonesLoading, error: timezonesError, refetch: retryTimezones } = useTimeZones();
  const [detectedTimezone] = useState(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } });
  const [chosenTimezone, setChosenTimezone] = useState(null);
  const timezoneOptions = React.useMemo(() => timeZones.filter(zone => canonicalTimezone(zone.timezone)).sort((a,b) => a.name.localeCompare(b.name)), [timeZones]);
  const signupTimezone = chosenTimezone || findTimezoneOption(timezoneOptions, detectedTimezone);
  const safeTimeZoneId = toFiniteIntOrNull(signupTimezone?.id);
  const location = useLocation();
  const brandName = "Timely365";
  const searchParams = new URLSearchParams(location.search);

  // 👇 read and sanitize returnTo (if the user arrived here from a protected action)
  const rawReturnTo = searchParams.get("returnTo");
  const safeReturnTo = getSafeReturnTo(rawReturnTo);
  // Normalize once so it's always absolute or null
  const safeReturnToNormalized = safeReturnTo
    ? (safeReturnTo.startsWith("/") ? safeReturnTo : `/${safeReturnTo}`)
    : null;
  const stateFrom =
    typeof location.state?.from === "string"
      ? getSafeReturnTo(location.state.from)
      : null;
  const storedFrom = getSafeReturnTo(localStorage.getItem("redirectAfterLogin"));
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const normalizePath = (path) => {
    if (!path) return null;
    try {
      return decodeURIComponent(path);
    } catch {
      return path;
    }
  };
  const isAuthPath = (path) =>
    !!normalizePath(path) &&
    /^\/(login|signup|forgotpassword)(?:[/?#]|$)/.test(normalizePath(path));
  const referrerPath = (() => {
    try {
      if (!document?.referrer) return null;
      const url = new URL(document.referrer);
      if (url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  })();
  const brandHref = "/";
  const handleBrandClick = (e) => {
    e.preventDefault();
    navigate("/", { replace: true });
  };

  const initialEmail = searchParams.get("email") || "";
  const pathname = window.location.pathname;
  const isTrial = pathname.includes("start-free-trial");

  const { countries: supportedCountries, loading: countriesLoading, error: countriesError } = useSupportedCountries();
  const [chosenCountry, setSignupCountry] = useState(null);
  const [detectedCountry] = useState(() => detectSignupCountry(detectedTimezone, navigator.languages || [navigator.language]));
  const signupCountry = chosenCountry ?? (supportedCountries.find(country => country.code === detectedCountry)?.code || '');
  const [email, setEmail] = useState(initialEmail);
  const [emailCode, setEmailCode] = useState('');
  const [emailProof, setEmailProof] = useState('');
  const [emailNotice, setEmailNotice] = useState('Verify your email to continue.');
  const [codeSent, setCodeSent] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const emailRevision = React.useRef(0);
  const validEmail = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email.trim()) && email.trim().length <= 254;
  const [requestEmailCode] = useMutation(gql`mutation RequestSignupEmailCode($email: String!) { requestSignupEmailCode(email: $email) }`);
  const [verifyEmailCode] = useMutation(gql`mutation VerifySignupEmailCode($email: String!, $code: String!) { verifySignupEmailCode(email: $email, code: $code) }`);
  const changeEmail = (value) => {
    emailRevision.current += 1;
    setEmail(value);
    setEmailProof('');
    setEmailCode('');
    setCodeSent(false);
    setVerifyingEmail(false);
    setEmailNotice('Verify your new email to continue.');
  };
  const handleEmailVerification = async (verifyCode = false) => {
    const revision = emailRevision.current;
    setVerifyingEmail(true);
    try {
      const variables = { email: email.trim().toLowerCase(), ...(verifyCode ? { code: emailCode.trim() } : {}) };
      const result = await (verifyCode ? verifyEmailCode : requestEmailCode)({ variables });
      if (revision !== emailRevision.current) return;
      if (verifyCode) {
        setEmailProof(result.data.verifySignupEmailCode);
        setEmailNotice('Email verified. You can create your account.');
      } else {
        setEmailProof('');
        setCodeSent(true);
        setEmailNotice('Enter the code sent to your email. It expires in 10 minutes.');
      }
    } catch (err) {
      if (revision === emailRevision.current) setEmailNotice(err.message || 'Verification failed. Please try again.');
    } finally {
      if (revision === emailRevision.current) setVerifyingEmail(false);
    }
  };
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [errors, setErrors] = useState([]);
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();

  const [signUpUser] = useMutation(SIGNUP_USER);
  const [signUpWithGoogleMutation] = useMutation(SIGNUP_USER_GOOGLE);

  const handleClose = () => setOpen(false);

  const handleGuideClose = () => setGuideOpen(false);

  // ✅ Always navigate with an absolute path, and replace history
  const goto = (path) => {
    const absolute = path.startsWith("/") ? path : `/${path}`;
    navigate(absolute, { replace: true });
  };

  const completeGoogleSignUp = async ({ email: googleEmail, name, credential }) => {
    if (!signupCountry) throw Error('Select your country before signing up.');
    if (!safeTimeZoneId) throw Error('Choose your timezone before signing up.');
    const result = await signUpWithGoogleMutation({
      variables: {
        email: googleEmail,
        credential,
        country: signupCountry,
        username: name || googleEmail,
        ...(safeTimeZoneId != null ? { timeZoneId: safeTimeZoneId } : {}),
      },
    });

    const token = result?.data?.signUpGoogle?.token;
    if (!token) throw new Error('Google sign up did not return a session. Please try again.');
    const user = jwtDecode(token);
    setToken(token);
    props.login(token);
    goto(getLoginDestination(user, safeReturnToNormalized, stateFrom, storedFrom));
  };

  const handleGoogleCredentialSuccess = async (credentialResponse) => {
    setErrors([]);
    try {
      const credential = credentialResponse?.credential;
      if (!credential) throw new Error("Missing Google credential.");
      const profile = jwtDecode(credential);
      if (!profile?.email) throw new Error("Google did not return an email.");
      await completeGoogleSignUp({ email: profile.email, name: profile.name, credential });
    } catch (err) {
      if (err.message === "Validation error") {
        setErrors(["Existing account with provided email"]);
      } else {
        setErrors([err.message || "Sign up with Google failed"]);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = [];
    if (creatingAccount) return;
    if (!emailProof) validationErrors.push('Verify your email to continue.');
    if (!signupCountry) validationErrors.push('Select your country.');
    if (!safeTimeZoneId) validationErrors.push(timezonesLoading ? 'Please wait for timezones to load.' : 'Choose your timezone.');

    if (!fullName.trim()) validationErrors.push("Full Name is required");
    if (!email.trim()) {
      validationErrors.push("Email is required");
    } else if (!validEmail) {
      validationErrors.push("Invalid Email Address");
    }
    if (!password.trim()) {
      validationErrors.push("Password is required");
    } else if (password.length < 6) {
      validationErrors.push("Password must be at least 6 characters long");
    }
    if (password !== passwordConfirm) {
      validationErrors.push("Passwords do not match");
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setCreatingAccount(true);
    signUpUser({
      variables: {
        email: email.trim().toLowerCase(),
        emailVerificationToken: emailProof,
        password,
        username: fullName,
        country: signupCountry,
        ...(safeTimeZoneId != null ? { timeZoneId: safeTimeZoneId } : {}),
      },
    })
      .then((result) => {
        setOpen(true);
        setTimeout(() => {
          handleClose();

          // Save token + mark logged in
          const token = result?.data?.signUp?.token;
          setToken(token);
          props.login(token);

          // ✅ Use normalized absolute navigation
          if (safeReturnToNormalized) {
            goto(safeReturnToNormalized);
          } else {
            goto("/workspaces"); // fallback page after sign-up
          }
        }, 500);
      })
      .catch((err) => {
        setErrors([err.message]);
        if (/verify your email/i.test(err.message)) {
          setEmailProof('');
          setCodeSent(false);
          setEmailNotice('Verify your email again to continue.');
        }
        setCreatingAccount(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 100px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        px: 2,
        py: { xs: 3, sm: 4 },
        backgroundColor: "background.paper",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 980,
          borderRadius: 4,
          border: "1px solid #dbe4ee",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.12)",
          backgroundColor: "background.paper",
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 5,
            background:
              "linear-gradient(160deg, #0b1f33 0%, #15314c 58%, #1b4464 100%)",
            color: "#ffffff",
          }}
        >
          <Box
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "inherit", textDecoration: "none" }}
          >
            <Box
              component="img"
              src="/images/timely365-icon.png"
              alt={brandName}
              sx={{ width: 42, height: 42, borderRadius: 1, backgroundColor: "background.paper" }}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: 0.4 }}>
              {brandName}
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 36, lineHeight: 1.1, fontWeight: 800, mb: 1.5 }}>
              {isTrial ? "Start your free 30-day trial." : "Make time for what matters."}
            </Typography>
            <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)", maxWidth: 360 }}>
              {isTrial
                ? "Try Timely365 for scheduling and bookings with no credit card required."
                : "Share your availability and let people book a time with you. For personal meetings, your business, or your team."}
            </Typography>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: 2,
              p: 2,
              backgroundColor: "rgba(255,255,255,0.10)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.95)" }}>
              Your availability. Your booking link. Your time.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            component={RouterLink}
            to={brandHref}
            onClick={handleBrandClick}
            sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, color: "#0891b2", mb: 1, textDecoration: "none", display: "inline-block" }}
          >
            {brandName}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}>
            Create your Timely365 account
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Set up your booking page and manage your meetings in one place.
          </Typography>

          <TextField select fullWidth label="Country" value={signupCountry} onChange={e=>setSignupCountry(e.target.value)} disabled={countriesLoading} helperText={countriesError ? 'Could not load countries. Refresh to try again.' : signupCountry ? 'Suggested from your device settings. Change it if needed.' : 'Choose your country. Timely is currently available in these countries.'} sx={{mb:2}}>{supportedCountries.map(c=><MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}</TextField>
          <SignupTimezone
            value={signupTimezone}
            options={timezoneOptions}
            loading={timezonesLoading}
            error={timezonesError}
            onChange={setChosenTimezone}
            onRetry={() => retryTimezones().catch(() => {})}
          />
          <form onSubmit={handleSubmit} style={{ marginBottom: "16px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 0.75 }}>Email</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Email"
                  type="email"
                  inputProps={{ 'aria-label': 'Email' }}
                  disabled={creatingAccount}
                  value={email}
                  onChange={(e) => changeEmail(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "background.paper" }}
                />
                <Typography role="status" variant="body2" sx={{ mt: 1, color: emailProof ? 'success.main' : 'text.secondary' }}>{emailNotice}</Typography>
                {!emailProof && <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button type="button" disabled={!validEmail || verifyingEmail || creatingAccount} onClick={() => handleEmailVerification(false)}>{codeSent ? 'Resend code' : 'Send verification code'}</Button>
                  {codeSent && <>
                    <TextField label="Verification code" value={emailCode} onChange={event => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }} size="small" />
                    <Button type="button" disabled={emailCode.length !== 6 || verifyingEmail || creatingAccount} onClick={() => handleEmailVerification(true)}>Verify email</Button>
                  </>}
                </Box>}
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 0.75 }}>Full Name</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "background.paper" }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 0.75 }}>Password</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "background.paper" }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 0.75 }}>Confirm Password</Typography>
                <OutlinedInput
                  fullWidth
                  placeholder="Confirm Password"
                  type={showPasswordConfirm ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "background.paper" }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPasswordConfirm((prev) => !prev)} edge="end">
                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Grid>
            </Grid>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!safeTimeZoneId || !validEmail || !emailProof || creatingAccount}
              fullWidth
              sx={{
                mt: 2.5,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                background: "linear-gradient(135deg, #0369a1 0%, #0f766e 100%)",
              }}
            >
              {creatingAccount ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2.25 }}>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "divider" }} />
            <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>OR</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: "divider" }} />
          </Box>

          <Box
            sx={{
              width: "100%",
              mb: 1,
            }}
          >
            {safeTimeZoneId ? <CenteredGoogleLogin
              onSuccess={handleGoogleCredentialSuccess}
              onError={() => setErrors(["Google sign up failed. Try again."])}
              text="continue_with"
              shape="rectangular"
              size="large"
              useOneTap={false}
            /> : <Typography variant="body2" color="text.secondary">Choose a timezone above to continue with Google.</Typography>}
          </Box>
          {errors.length > 0 && (
            <Box>
              {errors.map((err, idx) => (
                <Typography
                  key={idx}
                  align="center"
                  sx={{
                    mb: 0.5,
                    color: "error.main",
                    fontSize: 13,
                    backgroundColor: "action.hover",
                    border: "1px solid #fecaca",
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {err}
                </Typography>
              ))}
            </Box>
          )}

          <Backdrop open={open} style={{ zIndex: 9999, color: "#fff" }}>
            <CircularProgress />
            <Typography variant="h6" style={{ marginLeft: "10px" }}>
              Preparing your dashboard…
            </Typography>
          </Backdrop>

          <Typography align="center" variant="body2" sx={{ color: "text.secondary" }}>
            Already have an account? <a href="/login" style={{ fontWeight: 700 }}>Sign In</a>
          </Typography>
        </Box>
      </Paper>

      <GettingStartedGuide guideOpen={guideOpen} handleGuideClose={handleGuideClose} />
    </Box>
  );
}

export default SignUp;
